// Private intensive-course offers: pure helpers (no I/O) so they can be unit tested.
// Invitation lifetime and calendar-hold lifetime are deliberately separate.
export const INVITE_LIFETIME_HOURS = 7 * 24; // private link valid 7 days
export const HOLD_LIFETIME_HOURS = 48; // calendar holds (Phase 2) last exactly 48h
export const MAX_INVITE_LIFETIME_HOURS = 30 * 24;
export const DEFAULT_DEPOSIT_PENCE = 50000;

export function inviteExpiry(from: Date, hours = INVITE_LIFETIME_HOURS): Date {
  return new Date(from.getTime() + hours * 3600_000);
}

export function holdExpiry(from: Date): Date {
  return new Date(from.getTime() + HOLD_LIFETIME_HOURS * 3600_000);
}

/** 32 random bytes, base64url. Only its SHA-256 hash is ever stored. */
export function generateToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function hashToken(token: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export type OfferState = { status: string; revoked_at: string | null; link_expires_at: string };

/** Whether a learner link may still be used. */
export function linkUsable(o: OfferState, now: Date): { ok: true } | { ok: false; reason: 'revoked' | 'expired' | 'closed' } {
  if (o.revoked_at || o.status === 'revoked') return { ok: false, reason: 'revoked' };
  if (o.status === 'paid') return { ok: false, reason: 'closed' };
  if (new Date(o.link_expires_at).getTime() <= now.getTime()) return { ok: false, reason: 'expired' };
  return { ok: true };
}

/** Instructor management actions allowed per state. Paid offers are locked. */
export function canManage(action: 'revoke' | 'resend' | 'extend', o: OfferState): boolean {
  if (o.status === 'paid') return false;
  if (o.status === 'revoked' || o.revoked_at) return false;
  return action === 'revoke' || action === 'resend' || action === 'extend';
}

export type CreateInput = {
  learnerName?: string | null;
  learnerEmail?: string | null;
  learnerPhone?: string | null;
  courseHours: number;
  transmission: 'manual' | 'automatic';
  totalPricePence: number;
  depositPence?: number;
  toBeArranged?: boolean;
  schedulingWindowStart?: string | null;
  schedulingWindowEnd?: string | null;
  notes?: string | null;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown, max: number) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null);

export function validateCreate(body: any): { ok: true; value: CreateInput & { depositPence: number } } | { ok: false; error: string } {
  if (!body || typeof body !== 'object') return { ok: false, error: 'Invalid body' };
  const hours = Number(body.courseHours);
  if (!Number.isInteger(hours) || hours < 1 || hours > 100) return { ok: false, error: 'courseHours must be 1–100' };
  if (body.transmission !== 'manual' && body.transmission !== 'automatic') return { ok: false, error: 'transmission must be manual or automatic' };
  const total = Number(body.totalPricePence);
  if (!Number.isInteger(total) || total <= 0 || total > 2_000_000) return { ok: false, error: 'totalPricePence invalid' };
  const deposit = body.depositPence === undefined ? DEFAULT_DEPOSIT_PENCE : Number(body.depositPence);
  if (!Number.isInteger(deposit) || deposit <= 0 || deposit > total) return { ok: false, error: 'depositPence must be positive and not above the total' };
  const email = str(body.learnerEmail, 255);
  if (email && !EMAIL.test(email)) return { ok: false, error: 'learnerEmail invalid' };
  const ws = str(body.schedulingWindowStart, 10);
  const we = str(body.schedulingWindowEnd, 10);
  if ((ws && !DATE.test(ws)) || (we && !DATE.test(we))) return { ok: false, error: 'Scheduling window dates must be YYYY-MM-DD' };
  if (ws && we && we < ws) return { ok: false, error: 'Scheduling window end is before start' };
  return {
    ok: true,
    value: {
      learnerName: str(body.learnerName, 120),
      learnerEmail: email ? email.toLowerCase() : null,
      learnerPhone: str(body.learnerPhone, 30),
      courseHours: hours,
      transmission: body.transmission,
      totalPricePence: total,
      depositPence: deposit,
      toBeArranged: body.toBeArranged !== false,
      schedulingWindowStart: ws,
      schedulingWindowEnd: we,
      notes: str(body.notes, 1000),
    },
  };
}
